import React from 'react';
import { shape } from 'prop-types';
import { connect } from 'react-redux';
import { Query } from 'react-apollo';

import ErrorBoundary from 'components/ErrorBoundary';
import UserCompleteSignup from 'components/UserCompleteSignup';
import SignInPrompt from 'components/SignInPrompt';
import USER_QUERY from 'graphql/user/query';
import { formatWrappedComponentDisplayName } from 'util/hoc';

// Save errors on the client and catch on the server
const errorPolicy = process.browser ? 'all' : 'none';

/**
 * withUserAuthentication() should always be wrapped in a dynamic import because
 * it is only functional within the browser.
 */
export default WrappedComponent => {
  function WithUserAuthentication(props) {
    /**
     * we could test for process.browser === false here and return <Loading />,
     * but that would cause a server/client content mismatch and lead to page
     * rendering issues. using dynamic import mitigates those issues.
     */

    const { user } = props;

    if (!user) {
      return <SignInPrompt />;
    }

    if (!user.creationComplete) {
      return <UserCompleteSignup user={user} />;
    }

    const variables = {
      communityId: !user.auth0User ? user.communityId : null,
      auth0User: user.auth0User
    };

    return (
      <ErrorBoundary>
        <Query
          query={USER_QUERY}
          variables={variables}
          errorPolicy={errorPolicy}
          ssr={false}
        >
          {({ loading: isLoading, error, data }) => {
            if (error) {
              // eslint-disable-next-line no-console
              console.log(error);
            }

            const { user: userProfile } = data;

            return (
              <WrappedComponent
                {...props}
                isLoading={isLoading}
                user={user}
                userProfile={userProfile}
              />
            );
          }}
        </Query>
      </ErrorBoundary>
    );
  }

  WithUserAuthentication.displayName = formatWrappedComponentDisplayName(
    'withUserAuthentication',
    WrappedComponent
  );

  WithUserAuthentication.propTypes = {
    user: shape({
      // TBD
    })
  };

  WithUserAuthentication.defaultProps = {
    user: undefined
  };

  const mapStateToProps = ({ user }) => ({ user });

  return connect(mapStateToProps)(WithUserAuthentication);
};
